import { Lock, Link, Smartphone, ShieldCheck } from 'lucide-react';
import { ReactNode } from 'react';

import { Separator } from '@components/ui/separator';
import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { VStack, Flex } from '@components/ui/layout/flex';
import { ObfuscatedEmail } from '@components/ui/obfuscated-email';

interface FeatureProps {
  horizontal?: boolean;
  text: string;
  title?: string;
  icon: ReactNode;
  iconBg?: string;
}

function Feature({ horizontal, text, title, icon, iconBg = 'none' }: FeatureProps) {
  return horizontal ? (
    <VStack align="start">
      <Flex
        className="w-16 h-16 text-white rounded-full bg-gray-100 mb-1"
        align="center"
        justify="center"
      >
        {icon}
      </Flex>
      <Text className="font-semibold">{title}</Text>
      <Text className="text-gray-600">{text}</Text>
    </VStack>
  ) : (
    <Flex direction="row" align="center">
      <Flex
        className="w-8 h-8 rounded-full"
        align="center"
        justify="center"
        style={{ backgroundColor: iconBg }}
      >
        {icon}
      </Flex>
      <Text className="font-semibold">{text}</Text>
    </Flex>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="animate-in fade-in duration-300">
      <VStack className="min-h-screen flex-col md:flex-row">
        <div className="w-full">
          <div>
            <Heading level={2}>Privacy Policy</Heading>

            <VStack className="pt-4 items-start">
              <div className="content-start">
                <Heading level={4} className="py-4 text-gray-800 font-medium">
                  Principle of Least Privilege
                </Heading>
                <Text className="py-2 text-gray-700 text-lg font-light">
                  My ClinicAlly operates under the Principle of Least Privilege where we only have
                  access to the specific data that we need to complete a required task.
                </Text>
              </div>
            </VStack>
            <Heading level={4} className="py-12">
              We also promise:
            </Heading>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
              <Feature
                horizontal
                icon={<ShieldCheck className="w-10 h-10 text-orange-500" />}
                title="No Metadata Collection"
                text="We don't collect your browsing history, search queries, cookies or identifying metadata."
              />
              <Feature
                horizontal
                icon={<Link className="w-10 h-10 text-blue-500" />}
                title="Secure Form Links"
                text="We text you single-use secure form links that are unique to you. Once you've submitted your responses, the secure links are invalidated and no longer accessible."
              />
              <Feature
                horizontal
                icon={<Lock className="w-10 h-10 text-green-500" />}
                title="Encrypted Response Data"
                text="Your form responses are end-to-end encrypted and only viewable by your healthcare provider for decryption and viewing."
              />
              <Feature
                horizontal
                icon={<Smartphone className="w-10 h-10 text-purple-500" />}
                title="Easy Communication"
                text="Receive and exchange SMS or emailed messages from your provider."
              />
            </div>
          </div>

          <Separator className="my-4" />

          <div className="py-5">
            <Heading level={4} className="pb-1">
              Privacy Policy
            </Heading>
            <Text className="text-gray-500 text-sm italic font-semibold">
              Effective Date: May 4, 2026
            </Text>
          </div>

          <div>
            <Text className="text-lg pb-4">
              My ClinicAlly, a brand of abobotech LLC, (&quot;My ClinicAlly,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;) recognizes the importance of protecting the
              privacy of your personal information, and we have prepared this Privacy Policy
              (&quot;Policy&quot;) to provide you with important information about the privacy
              practices applicable to ourforms.co, myclinically.com, and any website
              (collectively, the &quot;Sites&quot;) or service(&quot;Service&quot;) that links to
              or refers to this Privacy Policy. By accessing the Sites or using the Service, you
              agree to be bound by this Policy. Each time you use any Site or Service, the current
              version of this Policy will apply. Accordingly, you should check the date of the
              Policy (which appears at the top) and review any changes since you last reviewed the
              Policy.
            </Text>

            <div>
              <Text className="text-base italic font-extrabold pt-5">
                My ClinicAlly provides services to both healthcare and non-healthcare
                organizations.
              </Text>

              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                WHEN HIPAA APPLIES TO YOUR INFORMATION
              </Heading>

              <Text>
                When you use our services through a healthcare provider (doctor, clinic, hospital),
                we operate as a HIPAA Business Associate.
              </Text>
              <Text className="pt-2 pb-4">
                When you use our services through a non-healthcare organization, HIPAA may not
                apply, but we still protect your information according to applicable privacy laws
                and our security commitments.
              </Text>

              <Text>
                If you&apos;re unsure whether HIPAA applies to your use of our services, contact us
                at <ObfuscatedEmail user="privacy" />.
              </Text>

              <Text className="pt-5 text-gray-500 text-base font-extrabold">
                When using My ClinicAlly through a healthcare provider, you have rights under HIPAA
                including:
              </Text>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your health information we maintain</li>
                <li>Request corrections if information is wrong</li>
                <li>Request we contact you differently</li>
                <li>Get a list of who we&apos;ve shared your information with</li>
                <li>Request limits on how we use your information</li>
                <li>File complaints about our privacy practices</li>
              </ul>

              <Text className="pt-5">
                Contact <ObfuscatedEmail user="privacy" /> or your healthcare provider to exercise these
                rights. Any requests we receive will be forwarded to your provider for review and
                response.
              </Text>

              <div>
                <Text className="pt-5 text-gray-500 text-base font-extrabold">
                  When using My ClinicAlly through a healthcare provider, we have responsibilities
                  under HIPAA including:
                </Text>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Keep your health information private and secure</li>
                  <li>Give you this notice of our privacy practices</li>
                  <li>Follow the terms of this notice</li>
                  <li>Notify you if your information is breached</li>
                </ul>
              </div>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                I. COLLECTION OF INFORMATION
              </Heading>
              <Text>We may collect the following kinds of information when you use the Services:</Text>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Information you provide directly to us-
              </Text>
              <Text>
                For certain activities, such as when you register, or contact us directly, we may
                collect some or all of the following types of information:
              </Text>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Contact information, such as your full name, and mobile phone number, or email
                  address.
                </li>
                <li>Any other information you voluntarily provide to us.</li>
              </ul>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Information we obtain from your health care providers and other sources-
              </Text>
              <Text>
                In connection with the Services, we may collect your full name and mobile number
                from you or your health care providers as well as any other information that is
                relevant to your care.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                II. USE OF INFORMATION
              </Heading>
              <Text className="pb-5">We generally use the information we collect online to:</Text>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fulfill your requests for Services, and information</li>
                <li>Help your healthcare provider communicate with you</li>
                <li>Support your healthcare provider&apos;s operations</li>
                <li>Provide and improve the Services</li>
                <li>Comply with legal requirements</li>
                <li>Contact you</li>
                <li>
                  Analyze the use of the Services and user data to understand and improve the
                  Services
                </li>
                <li>
                  Prevent potentially prohibited or illegal activities and otherwise in accordance
                  with our Terms of Use
                </li>
                <li>
                  For any other purposes disclosed to you at the time we collect your information
                  or pursuant to your consent
                </li>
              </ul>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-4">
                III. SHARING OF INFORMATION
              </Heading>
              <Text className="pb-5">
                We are committed to maintaining your trust, and we want you to understand when and
                with whom we may share the information we collect.
              </Text>
              <ul className="list-disc pl-6 space-y-3">
                <li>
                  <Text className="text-gray-500 text-base italic font-extrabold">
                    Healthcare providers, insurance companies, and other healthcare-related
                    entities -
                  </Text>
                  <Text>
                    We may share your Protected Health Information and other information with
                    health care providers, and other entities relevant to providing you with
                    Services.
                  </Text>
                </li>
                <li>
                  <Text className="text-gray-500 text-base italic font-extrabold">
                    Authorized third-party vendors and service providers -
                  </Text>
                  <Text>
                    We may share your information with third-party vendors and service providers
                    that help us with specialized services, including payment processing, data
                    processing or others. These third-party vendors and service providers may not
                    use your information for purposes other than those related to the services they
                    are providing to us.
                  </Text>
                </li>
                <li>
                  <Text className="text-gray-500 text-base italic font-extrabold">
                    Legal purposes -
                  </Text>
                  <Text>
                    We may disclose information to respond to subpoenas, court orders, legal
                    process, law enforcement requests, legal claims or government inquiries. Our
                    policy is to notify you of any requests for your information prior to
                    disclosure unless we are prohibited from doing so by statute or court order.
                  </Text>
                </li>
              </ul>
              <Text className="py-5">
                If you choose to engage in public activities on the Site or third-party sites that
                we link to, you should be aware that any information you share there can be read,
                collected, or used by other users. You should use caution in disclosing personal
                information while participating in these areas. We are not responsible for the
                information you choose to submit in these public areas.
              </Text>
              <Text className="text-lg">
                We will not sell your information to others or use it to advertise products to you.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                IV. SECURITY
              </Heading>
              <Text className="pb-5">
                We use reasonable measures to help protect information from loss, theft, misuse and
                unauthorized access, disclosure, alteration and destruction and ensure your data is
                as safe and private as our technology and ability allow. You should understand that
                no data storage system or transmission of data over the Internet or any other
                public network can be guaranteed to be 100 percent secure. Please note that
                information collected by third parties may not have the same security protections
                as information you submit to us, and we are not responsible for protecting the
                security of such information.
              </Text>
              <Text className="pb-5">
                My ClinicAlly strives to ensure that data breaches are responded to in an
                appropriate fashion, in accordance with the HIPAA breach notification rule and
                other applicable law. Individuals have the right to provide feedback on My
                ClinicAlly&apos;s compliance with the HIPAA Privacy Rule and its HIPAA privacy
                policies and procedures (&quot;Privacy Complaints&quot;). Individuals also have the
                right to provide feedback on the organization&apos;s breach notification process
                and compliance with the Breach Notification Rule. If you have concerns about our
                privacy practices, email us: <ObfuscatedEmail user="complaints" /> or file with HHS. We will
                not retaliate against you for filing a complaint.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                VI. CHANGES TO THE PRIVACY POLICY
              </Heading>
              <Text className="pb-5">
                We may update this Privacy Policy from time to time. When we update the Privacy
                Policy, we will revise the &quot;Effective Date&quot; date above and post the new
                Privacy Policy. We recommend that you review the Privacy Policy each time you visit
                the Services to stay informed of our privacy practices.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold py-6">
                VII. CONTACT INFORMATION
              </Heading>
              <Text className="pb-5">
                If you have any questions about this Privacy Policy or our practices, please email
                us at <ObfuscatedEmail user="team" />.
              </Text>
            </div>
          </div>
        </div>
      </VStack>
    </div>
  );
}
